import { MyleTableColumn } from "@/Common/Components/Tables/MyleTableColumn";
import { Guid } from "@/Common/Models/Guid";
import { LabInboxes, LabSortOrderType, LabTabViews, SortDirection } from "@/Common/Models/UI/Enums";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { LabMessage } from "@/Labs/Models/LabMessage";
import { LabMessageTableItem } from "@/Labs/Models/LabMessageTableItem";
import { LabsStore } from "@/Labs/Stores/LabsStore";

class LabsInboxViewHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalOperations.Labs.OnRefreshClick, this.RefreshLabs);
        this.RegisterOperation(LocalOperations.Labs.CheckLabMessageItem, this.CheckLabMessageItem);
        this.RegisterOperation(LocalOperations.Labs.CheckAllLabMessageItem, this.CheckAllLabMessageItem);
        this.RegisterOperation(LocalOperations.Labs.CurrentPageChange, this.CurrentPageChange);
        this.RegisterOperation(LocalOperations.Labs.SearchBoxTextChanged, this.SearchBoxTextChanged);
        this.RegisterOperation(LocalOperations.Labs.Table_InboxRowClick, this.Table_InboxRowClick);
        this.RegisterOperation(LocalOperations.Labs.Inbox_ButtonClick_FileLabs, this.Inbox_ButtonClick_FileLabs);
        this.RegisterOperation(LocalOperations.Labs.Inbox_ButtonClick_HoldLabs, this.Inbox_ButtonClick_HoldLabs);
        this.RegisterOperation(LocalOperations.Misc.Table_ColumnSortClick, this.SetColumnSort);
        this.RegisterOperation(LocalOperations.Labs.SwitchTo_OpenLab, this.ReturnToLab);
        this.RegisterOperation(LocalOperations.Labs.Inbox_ButtonClick_FileManualLabs, this.FileManualLabs);
    }

    public SetColumnSort(request: LocalEvent, store: LabsStore): void {
        const inboxIsManualLabs = store.LabsInboxModel.get("CurrentInbox") === LabInboxes.ManualResults;
        if (inboxIsManualLabs) {
            const currentState = store.getManualLabsInboxFilteringConfiguration();
            store.setManualLabsInboxFilteringConfiguration({ ...currentState, sortDirection: currentState.sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC });
            store.LabActions.RefreshLabs();
            return;
        }

        var tableTarget = request.Get<MyleTableTarget>("target");

        if (tableTarget === MyleTableTarget.Myle_LabInbox_Search_ColumnSortClick) {
            var columnOrderType = request.Get<LabSortOrderType>("sortColumn");

            if (store.LabsInboxModel.get("SortColumn") === columnOrderType) {
                // Same Column, Change Direction
                if (store.LabsInboxModel.get("SortColumnDirection") === SortDirection.ASC)
                    store.LabsInboxModel = store.LabsInboxModel.set("SortColumnDirection", SortDirection.DESC);
                else
                    store.LabsInboxModel = store.LabsInboxModel.set("SortColumnDirection", SortDirection.ASC);
            }
            else {
                store.LabsInboxModel = store.LabsInboxModel.set("SortColumn", columnOrderType);
                store.LabsInboxModel = store.LabsInboxModel.set("SortColumnDirection", SortDirection.DESC);
            }

            var column = request.Get<MyleTableColumn>("columnObject");

            if (column) {
                if (column.columnOptions.IsSortActive) {
                    column.ToggleSortDirection();
                }
                column.columnOptions.IsSortActive = true;
            }

            store.LabActions.RefreshLabs();
        }
    }

    public ReturnToLab(request: LocalEvent, store: LabsStore): void {
        store.Model = store.Model.set("CurrentView", LabTabViews.Lab);
    }

    private Table_InboxRowClick(request: LocalEvent): void {
        const lab = request.Get<LabMessage>("rowData").Model;
        labsStore.LabActions.GetLab(lab.LabFillerID, SubServerOperations.Labs.GetLab_Default, lab.IsManualLabEntry);
    }

    private Inbox_ButtonClick_FileLabs(request: LocalEvent, store: LabsStore): void {
        var item = request.Get<string[]>("item");
        store.LabActions.FileLabAction(item, true);
    }

    private Inbox_ButtonClick_HoldLabs(request: LocalEvent, store: LabsStore): void {
        var item = request.Get<string[]>("item");
        store.LabActions.HoldLabAction(item, true);
    }

    public SearchBoxTextChanged(request: LocalEvent, store: LabsStore): void {
        store.LabsInboxModel = store.LabsInboxModel.set("GlobalLabsearchText", request.Get<string>("value"));
        store.LabActions.RefreshLabs();
    }

    public RefreshLabs(request: LocalEvent, store: LabsStore): void {
        store.LabActions.RefreshLabs();
    }

    private CheckLabMessageItem(request: LocalEvent, store: LabsStore): void {
        const item = request.Get<LabMessageTableItem>("item");

        const newTableitems: LabMessageTableItem[] = store.LabsInboxModel.get("LabsTableData").toArray().map(labMessageItem => {
            if (labMessageItem.Model.Model.LabFillerID === item.Model.Model.LabFillerID)
                labMessageItem.IsSelected = !item.IsSelected;

            return labMessageItem;
        });

        let isAllSelected = false;

        if (store.LabsInboxModel.get("CurrentInbox") === LabInboxes.ManualResults) {
            isAllSelected = newTableitems.every(item => item.IsSelected);
        }
        else {
            isAllSelected = newTableitems.every(item => item.IsSelected || !item.isSelectable);
        }

        store.LabsInboxModel = store.LabsInboxModel
            .set("LabsTableData", Immutable.List(newTableitems))
            .set("SelectAllRowItemsChecked", isAllSelected);
    }

    private CheckAllLabMessageItem(request: LocalEvent, store: LabsStore): void {
        const valueToSet = request.Get<boolean>("checked");
        const newTableitems: LabMessageTableItem[] = store.LabsInboxModel.get("LabsTableData").toArray().map(labMessageItem => {
            if (store.LabsInboxModel.get("CurrentInbox") != LabInboxes.ManualResults) {
                if (labMessageItem.isSelectable)
                    labMessageItem.IsSelected = valueToSet;
            }
            else
                labMessageItem.IsSelected = valueToSet;

            return labMessageItem;
        });

        store.LabsInboxModel = store.LabsInboxModel
            .set("LabsTableData", Immutable.List<LabMessageTableItem>(newTableitems))
            .set("SelectAllRowItemsChecked", valueToSet);
    }

    private CurrentPageChange(request: LocalEvent, store: LabsStore): void {
        var value = request.Get<number>("Page");
        store.LabsInboxModel = store.LabsInboxModel.set("CurrentPage", value);

        store.LabActions.RefreshLabs();
    }

    private FileManualLabs(request: LocalEvent, store: LabsStore): void {
        const labFillerIds: Guid[] = request.Get<Guid[]>("item");
        store.LabActions.FileManualLabResults(labFillerIds, true);
    }
}

export { LabsInboxViewHandler };
